import { HttpEvent, HttpHandler, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';

/**
 * Bounds on silent access-token acquisition (#32087).
 *
 * The stock Auth0 `AuthHttpInterceptor` only calls `next.handle()` AFTER
 * `getTokenSilently()` resolves, so a silent renewal that never settles (a
 * hung hidden-iframe `/authorize`, a blocked `/oauth/token`, cross-tab lock
 * starvation) leaves the request unsent and the caller's Observable silent.
 * auth0-spa-js does bound its own steps (authorize iframe 60s by default,
 * fetch 10s, lock 5s x 10 retries), but the sum is a minute-long wait that
 * users experienced as an infinite spinner, and the eventual error reached
 * pages as an opaque non-HTTP error. MSAL bounds its iframe renewal but not
 * its token-endpoint fetch, so the Entra branch has the same exposure.
 *
 * Three numbers, one ordering rule: both SDK timeouts must be strictly shorter
 * than {@link TOKEN_ACQUIRE_BOUND_MS} so the SDK's own classified TimeoutError
 * (with its diagnostic) wins the race and the interceptor bound is the
 * backstop for the cases the SDK does not bound (lock waits, a hung iframe
 * load, a stalled MSAL fetch). Enforced by the acquire-timeout spec.
 */

/** auth0-spa-js `authorizeTimeoutInSeconds` (hidden-iframe `/authorize`). SDK default 60. */
export const AUTH0_AUTHORIZE_TIMEOUT_SECONDS = 15;
/** auth0-spa-js `httpTimeoutInSeconds` (`fetch` to `/oauth/token`). SDK default 10, made explicit. */
export const AUTH0_HTTP_TIMEOUT_SECONDS = 10;
/** Interceptor-level cap on the whole silent acquire, either issuer. */
export const TOKEN_ACQUIRE_BOUND_MS = 20_000;

export type TokenIssuer = 'auth0' | 'entra';

/**
 * How a silent acquire failed, in terms a page can act on:
 *  - `timeout`: the acquire did not settle in time (interceptor bound) or the
 *    SDK reported its own timeout. Reloading usually recovers.
 *  - `session-expired`: the identity provider says no token can be minted
 *    silently (`login_required`, `consent_required`, a missing or revoked
 *    refresh token). Only an interactive sign-in recovers.
 */
export type TokenAcquireFailureKind = 'timeout' | 'session-expired';

/**
 * The ONE error shape callers see for a failed silent acquire, whichever
 * issuer or SDK produced it. Deliberately NOT an `HttpErrorResponse`: no
 * request was sent and no status exists; fabricating a 401 would also
 * re-enter the interceptor's own 401-refresh path.
 */
export class TokenAcquireError extends Error {
  readonly code = 'token_acquire_failed';

  constructor(
    readonly issuer: TokenIssuer,
    readonly kind: TokenAcquireFailureKind,
    readonly inner?: unknown
  ) {
    super(
      kind === 'timeout'
        ? `Sign-in token could not be obtained within ${TOKEN_ACQUIRE_BOUND_MS / 1000}s (${issuer}); the request was not sent. Reload the page or sign in again.`
        : `Your sign-in session has expired (${issuer}); the request was not sent. Sign in again.`
    );
    this.name = 'TokenAcquireError';
  }
}

/** auth0-spa-js `TimeoutError` is `GenericError('timeout', 'Timeout')`. */
const AUTH0_TIMEOUT_CODES = new Set(['timeout']);
/**
 * auth0-spa-js codes meaning the SSO session cannot mint a token silently:
 * `login_required` / `consent_required` from the `/authorize` iframe,
 * `missing_refresh_token` (`MissingRefreshTokenError`, refresh-token renewal
 * with nothing cached), `invalid_grant` (refresh token expired or revoked).
 */
const AUTH0_SESSION_EXPIRED_CODES = new Set([
  'login_required',
  'consent_required',
  'missing_refresh_token',
  'invalid_grant',
]);

/**
 * Map an error thrown by the Auth0 acquire into a {@link TokenAcquireError}
 * when its `error` code is recognised; return the error UNCHANGED otherwise,
 * so an unfamiliar cause is never masked behind a wrong label. Matched on the
 * code, never the message, so an SDK wording change cannot un-match it.
 */
export function classifyAuth0AcquireFailure(err: unknown): unknown {
  if (err instanceof TokenAcquireError) {
    return err;
  }
  const code = (err as { error?: unknown } | null)?.error;
  if (typeof code !== 'string') {
    return err;
  }
  if (AUTH0_TIMEOUT_CODES.has(code)) {
    return new TokenAcquireError('auth0', 'timeout', err);
  }
  if (AUTH0_SESSION_EXPIRED_CODES.has(code)) {
    return new TokenAcquireError('auth0', 'session-expired', err);
  }
  return err;
}

/**
 * Run `delegate` (an interceptor that acquires a token and THEN calls
 * `next.handle`) under a bound that covers ONLY the acquire: the timer is
 * armed on subscribe and disarmed the moment the delegate hands the request
 * to `next`, so a slow API response is never cut off. If the bound elapses
 * before the request is issued the stream errors with `onTimeout()` and the
 * delegate is unsubscribed (its pending acquire is dropped).
 */
export function boundTokenAcquire(
  req: HttpRequest<unknown>,
  next: HttpHandler,
  delegate: (
    request: HttpRequest<unknown>,
    handler: HttpHandler
  ) => Observable<HttpEvent<unknown>>,
  boundMs: number,
  onTimeout: () => Error
): Observable<HttpEvent<unknown>> {
  return new Observable<HttpEvent<unknown>>((subscriber) => {
    let issued = false;
    let timer: ReturnType<typeof setTimeout> | undefined = setTimeout(() => {
      timer = undefined;
      if (!issued) {
        subscriber.error(onTimeout());
      }
    }, boundMs);
    const disarm = () => {
      if (timer !== undefined) {
        clearTimeout(timer);
        timer = undefined;
      }
    };
    const gatedNext: HttpHandler = {
      handle: (request) => {
        issued = true;
        disarm();
        return next.handle(request);
      },
    };
    const inner = delegate(req, gatedNext).subscribe(subscriber);
    return () => {
      disarm();
      inner.unsubscribe();
    };
  });
}
